import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header, PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Card } from '@/components/ui'
import { Languages, SettingsLanguages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  createSettingsLanguages,
  deleteSettingsLanguage,
  getAllLanguages,
  getSettingsLanguages,
  setDefaultSettingsLanguages,
} from '../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

interface ILanguagesValues {
  languages: {
    id: string
    locale: string
    name: string
    isDefault: boolean
  }[]
}

export const LanguagePage: React.FC = () => {
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const { editionId, festivalName } = useParams()
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [languages, setLanguages] = useState<SettingsLanguages[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [
    deleteConfirmationModalLanguages,
    setDeleteConfirmationModalLanguages,
  ] = useState<boolean>(false)
  const [removeLanguageState, setRemoveLanguagetState] = useState<number>(0)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const languagesResponse = await getSettingsLanguages()
        setLanguages(languagesResponse.data)
        setTimeout(() => {
          setValue('languages', languagesResponse.data)
        }, 300)
        const allLanguagesResponse = await getAllLanguages()
        setAllLanguages(allLanguagesResponse.data)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    languages: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required('Field is required'),
          locale: yup.string().required('Field is required'),
          name: yup.string().required('Field is required'),
          isDefault: yup.boolean().required('Field is required'),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useForm<ILanguagesValues>({
    mode: 'onChange',
    resolver: yupResolver<ILanguagesValues>(schema),
  })

  const watchLang = watch('languages')

  const {
    fields: languageFields,
    append: appendLanguages,
    remove: removeLanguages,
  } = useFieldArray({
    control: control,
    name: 'languages',
  })

  const submit = async (values: ILanguagesValues) => {
    try {
      if (values?.languages) {
        const payload = filter(
          map(values.languages, (l) => ({ languageId: l.id })),
          (l) => !find(languages, (lang) => lang.id === l.languageId),
        )
        const { status } = await createSettingsLanguages(payload)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
          const languagesResponse = await getSettingsLanguages()
          setLanguages(languagesResponse.data)
          setTimeout(() => {
            setValue('languages', languagesResponse.data)
          }, 300)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    setValue('languages', languages)
  }

  const deleteLanguagesFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        removeLanguages(removeLanguageState)
        if (watchLang?.[removeLanguageState]?.id !== '') {
          const { status } = await deleteSettingsLanguage(watchLang[removeLanguageState]?.id || '')
          if (status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
            const languagesResponse = await getSettingsLanguages()
            setLanguages(languagesResponse.data)
            setTimeout(() => {
              setValue('languages', languagesResponse.data)
            }, 300)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  return (
    <>
      <Header breadcrumbs={['Settings', 'Languages']} />
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        <form id="settings-languages-form" onSubmit={handleSubmit(submit)}>
          <h3 className="text-title-xs text-monochrome-1 mb-6">Languages</h3>
          <div className="flex flex-col items-start gap-4 w-full">
            {map(languageFields, (field, index) => (
              <FormGroup key={field.id}>
                <div className="flex gap-5 w-full">
                  <Controller
                    name={`languages.${index}.id`}
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        options={map(allLanguages, (lang) => ({
                          value: lang.id,
                          label: lang.name,
                        }))}
                        triggerClassName="!h-[44px] rounded-[16px]"
                        placeholder="Select Language"
                        outline
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value)
                          setValue(
                            `languages.${index}.locale`,
                            filter(allLanguages, (l) => l.id === value)[0]
                              ?.locale,
                          )
                          setValue(
                            `languages.${index}.name`,
                            filter(allLanguages, (l) => l.id === value)[0]
                              ?.name,
                          )
                          setValue(`languages.${index}.isDefault`, false)
                          setValue(`languages.${index}.isDefault`, false)
                        }}
                        disabled={!!languages[index] || !hasEditPermission}
                      />
                    )}
                  />
                  <Button
                    variant="secondary"
                    type="button"
                    size="sm"
                    className="h-[44px]"
                    disabled={field.isDefault || !hasEditPermission}
                    onClick={() => {
                      try {
                        setTimeout(() => {
                          map(languageFields, async (l, i) => {
                            if (i === index) {
                              const resp = await setDefaultSettingsLanguages(
                                watchLang[i].id,
                              )
                              if (resp.status === 200) {
                                setIsError(false)
                                setShowMessageBox(true)
                                setMessage('Default language change succesful')
                                const languagesResponse =
                                  await getSettingsLanguages()
                                setLanguages(languagesResponse.data)
                                setTimeout(() => {
                                  setValue('languages', languagesResponse.data)
                                }, 300)
                              }
                            }
                          })
                        }, 200)
                      } catch (e) {
                        if (e instanceof Error) {
                          setIsError(true)
                          setShowMessageBox(true)
                          setMessage(e.message)
                          setErrorStatus(
                            (e as { response?: { status?: number } })?.response
                              ?.status,
                          )
                        } else {
                          console.error(e)
                        }
                      }
                    }}
                  >
                    {field ? 'Default Language' : 'Set as Default'}
                  </Button>
                  {!field.isDefault && (
                    <Button
                      variant="destructive"
                      type="button"
                      size="sm"
                      className="h-[44px]"
                      disabled={!hasEditPermission}
                      onClick={() => {
                        setDeleteConfirmationModalLanguages(true)
                        setRemoveLanguagetState(index)
                      }}
                    >
                      Delete
                      <Icon
                        icon="close-circle"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        className="shrink-0"
                      />
                    </Button>
                  )}
                </div>
              </FormGroup>
            ))}
            <div className="flex justify-center items-center w-full">
              <Button
                onClick={() =>
                  appendLanguages({
                    id: '',
                    isDefault: false,
                    name: '',
                    locale: '',
                  })
                }
                type="button"
                variant="secondary"
                disabled={!hasEditPermission}
              >
                Add new
                <Icon
                  icon="plus"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                />
              </Button>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-languages-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmationModalLanguages}
        setDeleteConfirmationModal={setDeleteConfirmationModalLanguages}
        deleteTitle={'Language'}
        deleteFunc={deleteLanguagesFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
